/**
 * Handoff notes (collaboration phase 3): a short note for the next collaborator, what I did, what's next, open
 * questions, stored versioned in the project's brain (vfs-service `brain/.mia/handoffs.jsonl`).
 *
 * - The newest unread note by someone else is pinned first in the project Home's What changed (home/WhatChanged.jsx).
 * - HandoffNotesList ({ projectId, focusId?, onLeaveNote? }) (Team ▾ › Notes › Handoff notes; HandoffNotes, the same in
 *   a dialog of its own): the project's notes, newest first,
 *   All / For you / From you (vfs-service marks `forYou` and `fromYou`): where the writer finds a note they sent. Its
 *   writer or the project's owner (`canChange`) edits or deletes it for everyone (NoteChange).
 * - HandoffForm (Team ▾ › Notes › Leave a note; HandoffDialog ({ projectId, isOpen, onClose, projectName?, since?,
 *   intro? }) the same in a dialog of its own): "Leave a note": who it is for first
 *   (each gets an in-app notification; nobody picked, it only waits on the project Home), with "Fill from my changes"
 *   (a plain draft from your changes since you arrived) and "Ask the assistant to draft it" (a chat suggestion, never
 *   sent on its own).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import { addHandoff, deleteHandoff, editHandoff, getAccess, getHandoffDraft, getHandoffs } from '../../services/CollabService';
import NoteChange from './NoteChange';
import { draftPrompt } from '../../utils/collab3Model';
import { ago } from '../../utils/blameModel';
import { mentionables } from '../../utils/mentionModel';
import { getPresenceSnapshot } from '../../utils/presenceClient';
import './Collab.css';

/** One note's three parts. */
/** What a note is, in one line (the Team menu, the dialog and the Notes list say the same). */
export const NOTE_EXPLAINER = 'A note is a message to the people on this project: what you did, what is next, a question.';

export function NoteBody({ note }) {
  return (
    <div className="collab-note-body">
      {note.done && <p><strong>Done.</strong> {note.done}</p>}
      {note.next && <p><strong>Next.</strong> {note.next}</p>}
      {note.questions && <p><strong>Open questions.</strong> {note.questions}</p>}
    </div>
  );
}

NoteBody.propTypes = { note: PropTypes.object.isRequired };

const MAX = 2000;

/**
 * Leave a note's form: who it is for, the three parts, the helpers, then Not now / Send (`actionsClassName`: the dialog's
 * footer, or the Notes tab's own row). `onDone` after a send (the dialog closes, the Notes tab shows Handoff notes);
 * `onCancel` on Not now and after Ask the assistant.
 */
export function HandoffForm({ projectId, onDone, onCancel, projectName = '', since = null, intro = null, actionsClassName = 'modal-footer', onBusy = null }) {
  const [done, setDone] = useState('');
  const [next, setNext] = useState('');
  const [questions, setQuestions] = useState('');
  const [people, setPeople] = useState([]);
  const [to, setTo] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null); // null | the names it was sent to ([] for nobody)

  useEffect(() => {
    if (!projectId) { return undefined; }
    let cancelled = false;
    getAccess(projectId).then((access) => {
      if (cancelled) { return; }
      // Not yourself: the others in the project (vfs-service says who you are; else the presence socket).
      setPeople(mentionables(access, access?.you || getPresenceSnapshot(projectId)?.you || null).map((p) => ({ email: p.email, name: p.name })));
    }).catch(() => setPeople([]));
    return () => { cancelled = true; };
  }, [projectId]);
  useEffect(() => { onBusy?.(busy); }, [busy, onBusy]);

  const fill = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await getHandoffDraft(projectId, { since: since || undefined });
      if (answer?.draft) {
        setDone(String(answer.draft.done || '').slice(0, MAX));
        if (answer.draft.next) { setNext(String(answer.draft.next).slice(0, MAX)); }
        if (answer.draft.questions) { setQuestions(String(answer.draft.questions).slice(0, MAX)); }
      }
    } catch (e) {
      setError(e.message || 'The draft could not be made.');
    } finally {
      setBusy(false);
    }
  }, [projectId, since]);

  const askAssistant = () => {
    window.dispatchEvent(new CustomEvent('populate-chat-input', { detail: { message: draftPrompt(projectName, since), showSendPrompt: true } }));
    onCancel();
  };

  const submit = async () => {
    if (!done.trim()) { setError('Say at least what you did.'); return; }
    setBusy(true);
    setError(null);
    try {
      await addHandoff(projectId, { done: done.trim(), next: next.trim(), questions: questions.trim(), to, since: since || undefined });
      setSent(people.filter((p) => to.includes(p.email)).map((p) => p.name));
      setTimeout(onDone, 1800);
    } catch (e) {
      setError(e.message || 'The note could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const field = (label, value, set, testId, placeholder) => (
    <label className="collab-field">
      <span>{label}</span>
      <textarea data-testid={testId} maxLength={MAX} onChange={(e) => set(e.target.value)} placeholder={placeholder} value={value} />
    </label>
  );
  const toggle = (email, on) => setTo((cur) => (on ? [...cur, email] : cur.filter((x) => x !== email)));
  return (
    <>
      <div className="collab-handoff" data-testid="handoff-dialog">
        <p className="collab-muted collab-small" data-testid="handoff-explainer">{NOTE_EXPLAINER} The people you pick get a notification.</p>
        {intro && <p className="collab-muted">{intro}</p>}
        <div data-testid="handoff-to">
          <div className="collab-filter-title">For</div>
          {people.length > 0 ? (
            <div className="collab-to">
              {people.map((p) => (
                <label className="collab-radio" key={p.email} title={p.email}>
                  <input checked={to.includes(p.email)} onChange={(e) => toggle(p.email, e.target.checked)} type="checkbox" />
                  {p.name}
                </label>
              ))}
            </div>
          ) : <div className="collab-muted collab-small">Nobody else is on this project yet: share it first.</div>}
          {people.length > 0 && to.length === 0 && (
            <div className="collab-muted collab-small" data-testid="handoff-nobody">Nobody picked: no one gets a notification, the note waits on the project Home for whoever opens it next.</div>
          )}
        </div>
        {field('What I did', done, setDone, 'handoff-done', 'Map filters are done; the API still ignores radius.')}
        {field('What’s next', next, setNext, 'handoff-next', 'Add radius to GET /stores.')}
        {field('Open questions', questions, setQuestions, 'handoff-questions', 'Should the title say “near you” without a city?')}
        <div className="collab-handoff-helpers">
          <button className="collab-btn" data-testid="handoff-fill" disabled={busy} onClick={fill} type="button">Fill from my changes</button>
          <button className="collab-btn" data-testid="handoff-ask-assistant" onClick={askAssistant} type="button">Ask the assistant to draft it</button>
        </div>
        {error && <div className="collab-error collab-small">{error}</div>}
        {sent && (
          <div className="collab-small" data-testid="handoff-sent">
            {sent.length > 0
              ? `Note sent. ${sent.join(', ')} ${sent.length === 1 ? 'gets' : 'get'} a notification; you find it in Team › Notes › Handoff notes.`
              : 'Note saved. It waits on the project Home for whoever opens it next; you find it in Team › Notes › Handoff notes.'}
          </div>
        )}
      </div>
      <div className={actionsClassName}>
        <button className="modal-btn modal-btn-cancel" disabled={busy} onClick={onCancel} type="button">Not now</button>
        <button className="modal-btn modal-btn-confirm" data-testid="handoff-submit" disabled={busy || Boolean(sent)} onClick={submit} type="button">{to.length > 0 ? 'Send the note' : 'Leave the note'}</button>
      </div>
    </>
  );
}

HandoffForm.propTypes = {
  actionsClassName: PropTypes.string,
  intro: PropTypes.string,
  onBusy: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  projectName: PropTypes.string,
  since: PropTypes.string,
};

export function HandoffDialog({ projectId, isOpen, onClose, projectName = '', since = null, intro = null }) {
  const [busy, setBusy] = useState(false);
  if (!isOpen) { return null; }
  return (
    <Modal className="collab-handoff-modal" isOpen onClose={busy ? null : onClose} title="Leave a note" type="custom">
      <HandoffForm intro={intro} onBusy={setBusy} onCancel={onClose} onDone={onClose} projectId={projectId} projectName={projectName} since={since} />
    </Modal>
  );
}

HandoffDialog.propTypes = {
  intro: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  projectName: PropTypes.string,
  since: PropTypes.string,
};

const NOTE_FILTERS = [['all', 'All'], ['for', 'For you'], ['from', 'From you']];
const NOTE_FIELDS = [{ key: 'done', label: 'What I did' }, { key: 'next', label: 'What’s next' }, { key: 'questions', label: 'Open questions' }];

/** "for Davide", "for you", "for whoever comes next" (the row starts with its writer, "You" for yours). */
export function noteAudience(note) {
  const names = (note.to || []).map((p) => (note.forYou && (note.to || []).length === 1 ? 'you' : p.name || p.email));
  return names.length ? `for ${names.join(', ')}` : 'for whoever comes next';
}

/**
 * The project's notes, All / For you / From you, newest first (Team ▾ › Notes › Handoff notes, and HandoffNotes).
 * `onLeaveNote`: Leave a note, on top.
 */
export function HandoffNotesList({ projectId, focusId = null, onLeaveNote = null }) {
  const [notes, setNotes] = useState(null);
  const [filter, setFilter] = useState('all');
  const load = useCallback(() => getHandoffs(projectId, { limit: 100 }).then((answer) => answer?.notes || []).catch(() => []), [projectId]);
  useEffect(() => {
    if (!projectId) { return undefined; }
    let cancelled = false;
    load().then((list) => { if (!cancelled) { setNotes(list); } });
    return () => { cancelled = true; };
  }, [projectId, load]);
  const reload = () => load().then(setNotes);
  const shown = (notes || []).filter((n) => filter === 'all' || (filter === 'for' ? n.forYou : n.fromYou));
  const empty = filter === 'for' ? 'No notes for you yet.' : filter === 'from' ? 'You have not left a note here yet.' : 'No notes yet.';
  return (
    <div className="collab-handoff" data-testid="handoff-notes">
      <div className="collab-messages-bar">
        <div aria-label="Show" className="collab-seg" role="group">
          {NOTE_FILTERS.map(([value, label]) => (
            <button aria-pressed={filter === value} className={filter === value ? 'is-on' : ''} key={value} onClick={() => setFilter(value)} type="button">{label}</button>
          ))}
        </div>
        {onLeaveNote && <button className="collab-btn is-primary" data-testid="notes-leave" onClick={onLeaveNote} type="button">Leave a note</button>}
      </div>
      {notes === null && <div className="collab-muted">Loading…</div>}
      {notes && shown.length === 0 && (
        <div className="collab-messages-empty" data-testid="notes-empty">
          <strong>{empty}</strong>
          <span>{NOTE_EXPLAINER} The ones you sent and the ones for you show here.</span>
        </div>
      )}
      {shown.map((note) => (
        <div className={`collab-thread${note.id === focusId ? ' is-active' : ''}`} data-testid="handoff-note" key={note.id}>
          <div><strong>{note.fromYou ? 'You' : note.by?.name || 'Someone'}</strong> <span className="collab-muted collab-small">· {ago(note.at)} · {noteAudience(note)}{note.editedAt ? ' · edited' : ''}</span></div>
          <NoteBody note={note} />
          {note.canChange && (
            <NoteChange
              fields={NOTE_FIELDS}
              onChanged={reload}
              onDelete={() => deleteHandoff(projectId, note.id)}
              onSave={(values) => editHandoff(projectId, note.id, values)}
              values={{ done: note.done || '', next: note.next || '', questions: note.questions || '' }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

HandoffNotesList.propTypes = {
  focusId: PropTypes.string,
  onLeaveNote: PropTypes.func,
  projectId: PropTypes.string,
};

export function HandoffNotes({ projectId, isOpen, onClose, focusId = null, onLeaveNote = null }) {
  if (!isOpen) { return null; }
  const leave = onLeaveNote ? () => { onClose(); onLeaveNote(); } : null;
  return (
    <Modal className="collab-handoff-modal" isOpen onClose={onClose} title="Notes" type="custom">
      <HandoffNotesList focusId={focusId} onLeaveNote={leave} projectId={projectId} />
    </Modal>
  );
}

HandoffNotes.propTypes = {
  focusId: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLeaveNote: PropTypes.func,
  projectId: PropTypes.string,
};
